import React, { useState, useEffect } from 'react';
import LessonLayout from '../components/LessonLayout';
import { Card, Row, Col, Alert, Button, Form, ListGroup, Badge } from 'react-bootstrap';

const Lesson06_Lists = () => {
  const [items, setItems] = useState([
    { id: 1, name: 'Apple', category: 'Fruit', price: 120 },
    { id: 2, name: 'Banana', category: 'Fruit', price: 40 },
    { id: 3, name: 'Carrot', category: 'Vegetable', price: 60 },
    { id: 4, name: 'Tomato', category: 'Vegetable', price: 30 },
  ]);

  const [newItemName, setNewItemName] = useState('');
  const [filterText, setFilterText] = useState('');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    console.log('📖 Lesson 06: Lists & Keys - Loaded');
    console.log('📚 Initial items:', items);
  }, []);

  const addItem = () => {
    if (newItemName.trim()) {
      const newItem = {
        id: Date.now(),
        name: newItemName,
        category: 'Other',
        price: Math.floor(Math.random() * 100) + 20
      };
      console.log('➕ Adding new item:', newItem);
      setItems([...items, newItem]);
      setNewItemName('');
    }
  };

  const removeItem = (id) => {
    console.log('➖ Removing item with id:', id);
    setItems(items.filter(item => item.id !== id));
  };

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(filterText.toLowerCase())
  );

  const sortedItems = [...filteredItems].sort((a, b) => {
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    if (sortBy === 'price') return a.price - b.price;
    return 0;
  });

  console.log('🔍 Filtered items count:', filteredItems.length);
  console.log('📊 Sorted by:', sortBy);

  return (
    <LessonLayout 
      lessonNumber={6}
      title="Lists & Keys" 
      description="Learn to render lists efficiently with proper key management"
    >
      <Alert variant="success">
        <strong>What You'll Learn:</strong>
        <ul className="mb-0 mt-2">
          <li>Rendering arrays with map()</li>
          <li>Importance of key prop</li>
          <li>Filtering and sorting lists</li>
          <li>Adding and removing items from lists</li>
          <li>Why index as key is an anti-pattern</li>
        </ul>
      </Alert>

      <Row className="mt-4">
        <Col md={4}>
          <Card className="mb-3">
            <Card.Header>Add New Item</Card.Header>
            <Card.Body>
              <Form.Group className="mb-2">
                <Form.Control
                  type="text"
                  placeholder="Item name"
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addItem()}
                />
              </Form.Group>
              <Button variant="primary" onClick={addItem} className="w-100">
                Add Item
              </Button>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="mb-3">
            <Card.Header>Filter Items</Card.Header>
            <Card.Body>
              <Form.Control
                type="text"
                placeholder="Search items..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="mb-3">
            <Card.Header>Sort By</Card.Header>
            <Card.Body>
              <Form.Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="name">Name</option>
                <option value="price">Price</option>
              </Form.Select>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-3">
        <Card.Header>
          Items List <Badge bg="primary">{sortedItems.length} items</Badge>
        </Card.Header>
        <Card.Body>
          <ListGroup>
            {sortedItems.map((item) => {
              console.log('🔑 Rendering item with key:', item.id);
              return (
                <ListGroup.Item key={item.id} className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{item.name}</strong>
                    <Badge bg="secondary" className="ms-2">{item.category}</Badge>
                    <Badge bg="success" className="ms-2">₹{item.price}</Badge>
                  </div>
                  <Button variant="outline-danger" size="sm" onClick={() => removeItem(item.id)}>
                    Remove
                  </Button>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
          {sortedItems.length === 0 && (
            <Alert variant="info" className="mt-3 mb-0">No items found. Try adjusting the filter or add new items.</Alert>
          )}
        </Card.Body>
      </Card>

      <h4 className="mt-4">📊 Key Prop Importance</h4>
      <Card>
        <Card.Body>
          <table className="table comparison-table">
            <thead>
              <tr>
                <th>Key Type</th>
                <th>Example</th>
                <th>Stability</th>
                <th>Recommendation</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Unique ID</td>
                <td><code>key={'{'}item.id{'}'}</code></td>
                <td>✅ Stable</td>
                <td>✅ Best practice</td>
              </tr>
              <tr>
                <td>Index</td>
                <td><code>key={'{'}index{'}'}</code></td>
                <td>❌ Unstable</td>
                <td>⚠️ Avoid if list changes</td>
              </tr>
              <tr>
                <td>Random</td>
                <td><code>key={'{'}Math.random(){'}'}</code></td>
                <td>❌ Never stable</td>
                <td>❌ Never use</td>
              </tr>
            </tbody>
          </table>
        </Card.Body>
      </Card>

      <Alert variant="warning" className="mt-4">
        <strong>💡 Best Practices:</strong>
        <ul className="mb-0">
          <li>Always use unique IDs as keys (database IDs, UUIDs, etc.)</li>
          <li>Keys must be stable across re-renders</li>
          <li>Don't use array index as key if list can be reordered/filtered</li>
          <li>Keys only need to be unique among siblings, not globally</li>
        </ul>
      </Alert>
    </LessonLayout>
  );
};

export default Lesson06_Lists;
